import { useSubchapterValidations } from "@/hooks/useSubchapterValidations";
import type { ValidationSubmission } from "@/lib/validationSubmission";

/**
 * Loads the learner's saved validated submissions (input + last verdict) for a
 * content element, keyed by entry index, so an editor/textarea can be re-seeded
 * on reopen. Used by the retryable, server-persisted code quizzes (CODE_ASSERTION,
 * CODE_EXPLANATION, CODE_DEBUG). Shares the SWR cache key with {@link useQuizValidations}
 * (per subchapter). `refresh` revalidates after a new submission is persisted.
 */
export const useValidationSubmissions = (
  contentElementId: string
): {
  submissions: Record<number, ValidationSubmission>;
  refresh: () => void;
  /** False while the first validation_data fetch is in flight. */
  isReady: boolean;
} => {
  const { data, mutate, isLoading } =
    useSubchapterValidations(contentElementId);

  const submissions: Record<number, ValidationSubmission> = {};
  for (const item of data ?? []) {
    if (item.contentElementId !== contentElementId) continue;
    submissions[item.entryIndex] = {
      userInput: item.userInput,
      correct: item.result.correct ?? null,
      grade: item.result.grade,
      feedback: item.result.feedback ?? "",
      attemptCount: item.attemptCount,
      maxAttempts: item.maxAttempts,
    };
  }

  return { submissions, refresh: () => void mutate(), isReady: !isLoading };
};
